import React, { useState, useEffect } from 'react';
import { getFirestore, collection, getDocs, deleteDoc, doc, updateDoc, query, orderBy } from 'firebase/firestore';
import { getStorage, ref, deleteObject } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../config/firebase';
import { signOut } from 'firebase/auth';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableDealCard } from './SortableDealCard';
import AddButton from '../../components/AddButton/AddButton';
import CreateDealForm from '../../components/CreateDealForm/CreateDealForm';
import EditDealForm from '../../components/EditDealForm/EditDealForm';
import './Deals.css';

const Deals = () => {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [dealToEdit, setDealToEdit] = useState(null);
  
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('dealsViewMode') || 'grid';
  });
  
  const [sortOrderDate, setSortOrderDate] = useState(() => { 
    return localStorage.getItem('dealsSortOrderDate') || 'desc';
  });

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    localStorage.setItem('dealsViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('dealsSortOrderDate', sortOrderDate);
  }, [sortOrderDate]);

  const fetchDeals = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      const dealsRef = collection(db, 'deals');
      const q = query(dealsRef, orderBy('order', 'asc')); 
      const snapshot = await getDocs(q);
      const dealsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setDeals(dealsList);
    } catch (error) {
      console.error("Erreur lors de la récupération des bons plans:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeals();
  }, []);

  const initializeOrder = async () => {
    try {
      const db = getFirestore();
      const dealsRef = collection(db, 'deals');
      const snapshot = await getDocs(dealsRef);

      const sortedDocs = snapshot.docs.sort((a, b) => {
        const dateA = a.data().dateCreation ? new Date(a.data().dateCreation) : new Date(0);
        const dateB = b.data().dateCreation ? new Date(b.data().dateCreation) : new Date(0);
        return dateB - dateA;
      });

      const batch = db.batch();
      sortedDocs.forEach((dealDoc, index) => {
        const dealRef = doc(db, 'deals', dealDoc.id);
        batch.update(dealRef, { order: index });
      });

      await batch.commit();
      await fetchDeals();
    } catch (error) {
      console.error("Erreur lors de l'initialisation de l'ordre des deals:", error);
    }
  };

  useEffect(() => {
    const initializeDealsOrderOnce = async () => {
      const hasInitialized = localStorage.getItem('dealsOrderInitialized');
      if (!hasInitialized) {
        await initializeOrder();
        localStorage.setItem('dealsOrderInitialized', 'true');
      }
    };
    initializeDealsOrderOnce();
  }, []);

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      const db = getFirestore();
      const storage = getStorage();
      if (itemToDelete.imageUrl) {
        const imageRef = ref(storage, itemToDelete.imageUrl);
        await deleteObject(imageRef).catch(error => {
          console.log("L'image n'existe peut-être plus dans le storage:", error);
        });
      }
      await deleteDoc(doc(db, 'deals', itemToDelete.id));
      await fetchDeals();
      setShowDeleteConfirm(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Erreur lors de la suppression du deal:', error);
      alert('Une erreur est survenue lors de la suppression');
    } finally {
      setIsDeleting(false);
    }
  };
  
  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  const handleEditClick = (deal) => {
    setDealToEdit(deal);
    setShowEditForm(true);
  };
  
  const handleEditSuccess = async () => {
    setShowEditForm(false);
    setDealToEdit(null);
    await fetchDeals();
  };
  
  const handleCreateSuccess = async () => {
    setShowCreateForm(false);
    await fetchDeals();
  };

  const sortedDeals = [...deals].sort((a, b) => {
    const orderA = typeof a.order === 'number' ? a.order : Infinity;
    const orderB = typeof b.order === 'number' ? b.order : Infinity;
    return orderA - orderB;
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setDeals((currentDeals) => {
        const oldIndex = currentDeals.findIndex((item) => item.id === active.id);
        const newIndex = currentDeals.findIndex((item) => item.id === over.id);

        if (oldIndex === -1 || newIndex === -1) {
          return currentDeals;
        }
        let movedItems = arrayMove(currentDeals, oldIndex, newIndex);
        movedItems = movedItems.map((item, index) => ({
          ...item,
          order: index,
        }));

        const db = getFirestore();
        movedItems.forEach(async (item) => {
          const itemRef = doc(db, 'deals', item.id);
          await updateDoc(itemRef, { order: item.order });
        });
        return movedItems;
      });
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  return (
    <div className="deals">
      <div className="deals-content">
        <div className="dashboard-container">
          <main className="main-content">
            <div className="content-header">
              <h1>Bons Plans</h1>
              <div className="header-actions">
                <div className="sort-filter">
                  <select
                    value={sortOrderDate}
                    onChange={(e) => setSortOrderDate(e.target.value)}
                    className="sort-select"
                  >
                    <option value="desc">Plus récent (Date)</option>
                    <option value="asc">Plus ancien (Date)</option>
                  </select>
                </div>
                <div className="view-toggle">
                  <button 
                    className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => setViewMode('grid')}
                  >
                    <i className="fas fa-th-large"></i>
                  </button>
                  <button 
                    className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                    onClick={() => setViewMode('list')}
                  >
                    <i className="fas fa-list"></i>
                  </button>
                </div>
                <div className="button-container">
                  <AddButton onClick={() => setShowCreateForm(true)} />
                </div>
              </div>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={sortedDeals.map(item => item.id)}
                strategy={viewMode === 'grid' ? rectSortingStrategy : verticalListSortingStrategy}
              >
                <div className={`deals-container ${viewMode}`}>
                  {loading ? (
                    <div className="loading">Chargement...</div>
                  ) : sortedDeals.length === 0 ? (
                    <div className="no-results">Aucun bon plan pour le moment.</div>
                  ) : (
                    sortedDeals.map(deal => (
                      <SortableDealCard
                        key={deal.id}
                        id={deal.id}
                        item={deal}
                        viewMode={viewMode}
                        onEdit={handleEditClick}
                        onDelete={handleDeleteClick}
                        isDeleting={isDeleting && itemToDelete?.id === deal.id}
                        itemToDelete={itemToDelete}
                      />
                    ))
                  )}
                </div>
              </SortableContext>
            </DndContext>

            {showCreateForm && (
              <CreateDealForm 
                onClose={() => setShowCreateForm(false)}
                onSuccess={handleCreateSuccess}
              />
            )}
            
            {showEditForm && dealToEdit && (
              <EditDealForm
                deal={dealToEdit}
                onClose={() => {
                  setShowEditForm(false);
                  setDealToEdit(null);
                }}
                onSuccess={handleEditSuccess}
              />
            )}

            {showDeleteConfirm && itemToDelete && (
              <div className="modal-overlay">
                <div className="confirm-modal">
                  <h3>Confirmer la suppression</h3>
                  <p>Êtes-vous sûr de vouloir supprimer "{itemToDelete.titre}" ?</p>
                  <p className="warning-text">Cette action est irréversible.</p>
                  <div className="confirm-actions">
                    <button 
                      className="cancel-btn"
                      onClick={handleDeleteCancel}
                      disabled={isDeleting}
                    >
                      Annuler
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={handleDeleteConfirm}
                      disabled={isDeleting}
                    >
                      {isDeleting ? 'Suppression...' : 'Supprimer'}
                    </button>
                  </div>
                </div>
              </div>
            )}
            
          </main>
        </div>
      </div>
    </div>
  );
};

export default Deals; 